import { useEffect, useRef, useState } from 'react'
import { Platform } from 'react-native'
import { usePathname } from 'expo-router'
import { useSupabaseAuth } from '../../../shared/contexts/SupabaseAuthContext'
import { useApiClient } from '../../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../../constants/api'
import { useOperatorWebPaletteUiStore } from '../../stores/operatorWebPaletteUiStore'

interface AccessEnvelope {
  success?: boolean
  data?: { isZportiAdmin?: boolean }
}

/**
 * Web: Cmd+K (mac) / Ctrl+K (Windows) toggles the operator palette overlay on the marketing site
 * (`/`, `/privacy`, …) without changing the URL. Only registers the listener when the
 * signed-in user is a confirmed Zporti admin -- non-admins never see a hint of this feature.
 *
 * Uses useEffect (not useFocusEffect) because this lives in the root layout, not a screen.
 */
export function WebOperatorPaletteShortcut() {
  const pathname = usePathname() ?? ''
  const { isSignedIn, isLoaded } = useSupabaseAuth()
  const apiClient = useApiClient()
  const [isAdmin, setIsAdmin] = useState(false)
  const checkedRef = useRef(false)

  useEffect(() => {
    if (!isLoaded || !isSignedIn) {
      setIsAdmin(false)
      checkedRef.current = false
      return
    }
    if (checkedRef.current) return
    checkedRef.current = true

    let cancelled = false
    void (async () => {
      try {
        const res = await apiClient<AccessEnvelope>(API_ENDPOINTS.zportiAdmin.access)
        if (!cancelled) setIsAdmin(Boolean(res.data?.data?.isZportiAdmin))
      } catch {
        if (!cancelled) setIsAdmin(false)
      }
    })()
    return () => { cancelled = true }
  }, [isLoaded, isSignedIn, apiClient])

  const shouldListen = Platform.OS === 'web'
    && typeof document !== 'undefined'
    && !pathname.startsWith('/app')
    && isAdmin

  useEffect(() => {
    if (!shouldListen) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return
      if (e.key !== 'k' && e.key !== 'K') return
      const el = document.activeElement as HTMLElement | null
      const tag = el?.tagName?.toLowerCase()
      if (tag === 'input' || tag === 'textarea' || tag === 'select' || el?.isContentEditable) {
        return
      }
      e.preventDefault()
      useOperatorWebPaletteUiStore.getState().toggle()
    }

    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [shouldListen])

  return null
}
